/**
 * Helpers for TipTap Markdown messaging editors.
 */

/** Treat blank / whitespace-only Markdown (and empty ProseMirror docs) as empty. */
export function isMarkdownEmpty(markdown) {
  if (markdown == null) return true;
  const text = String(markdown)
    .replace(/```[\s\S]*?```/g, "x")
    .replace(/`[^`]+`/g, "x")
    .replace(/!\[[^\]]*]\([^)]*\)/g, "x")
    .replace(/\[[^\]]*]\([^)]*\)/g, "x")
    .replace(/[#>*_~`\-[\]()]/g, " ")
    .replace(/\s+/g, "")
    .trim();
  return text.length === 0;
}

/** Normalize editor Markdown before API submit / draft compare. */
export function normalizeEditorMarkdown(markdown) {
  if (markdown == null) return "";
  return String(markdown).replace(/\r\n/g, "\n").trim();
}

/**
 * Strip noisy Word/Docs paste markup before TipTap parses HTML.
 * Schema still drops unsupported nodes; this removes style/color/image noise.
 */
export function sanitizePastedHtml(html) {
  if (!html || typeof html !== "string") return "";
  if (typeof DOMParser === "undefined") return html;

  const doc = new DOMParser().parseFromString(html, "text/html");
  doc
    .querySelectorAll("script, style, meta, link, img, picture, video, audio, iframe, object, embed, svg")
    .forEach((el) => el.remove());

  doc.querySelectorAll("*").forEach((el) => {
    for (const attr of [...el.attributes]) {
      const name = attr.name.toLowerCase();
      if (
        name === "style" ||
        name === "class" ||
        name.startsWith("on") ||
        name === "color" ||
        name === "bgcolor" ||
        name === "face" ||
        name === "size" ||
        name.startsWith("data-")
      ) {
        el.removeAttribute(attr.name);
      }
    }
  });

  // Unwrap font/span that only carried styling
  doc.querySelectorAll("font, span").forEach((el) => {
    const parent = el.parentNode;
    if (!parent) return;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  });

  return doc.body?.innerHTML || "";
}

export function isSafeHttpUrl(url) {
  if (!url || typeof url !== "string") return false;
  try {
    const parsed = new URL(url, "https://example.invalid");
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/** Allow http(s) plus app-internal /o/... reference paths in the editor. */
export function isAllowedEditorHref(url) {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  if (
    /^\/o\/[^/]+\/(members\/[0-9a-fA-F-]{36}|messages\/\d+)$/.test(trimmed)
  ) {
    return true;
  }
  return isSafeHttpUrl(trimmed);
}
